import React, { useState, useEffect } from 'react';
import api from '../services/api.js';
import Message from '../components/Message.jsx';
import CommonTable from '../components/common/CommonTable.jsx';

const getOrdinalMonth = (num) => {
  const n = parseInt(num, 10);
  if (isNaN(n)) return `${num || ''} Month`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]} Month`;
};

const PaymentTerms = () => {
  const [terms, setTerms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ 
    title: '', 
    installments: [{ installmentMonth: '', paymentPercentage: '' }] 
  });
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionLoadingText, setActionLoadingText] = useState("Processing...");
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

const [paymentFilters, setPaymentFilters] = useState({
  title: "",
  installment: "",
});

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const data = await api.paymentTerms.getAll();
      setTerms(data);
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate installment months selection and increasing order
    for (let i = 0; i < formData.installments.length; i++) {
      const month = formData.installments[i].installmentMonth;
      if (month === '' || month === undefined || month === null) {
        setMessage({ text: `Please select a month for Installment ${i + 1}`, type: 'error' });
        return;
      }
    }

    for (let i = 1; i < formData.installments.length; i++) {
      const prevMonth = parseInt(formData.installments[i - 1].installmentMonth, 10);
      const currMonth = parseInt(formData.installments[i].installmentMonth, 10);
      if (currMonth <= prevMonth) {
        setMessage({
          text: `Installment months must be in increasing order! Row ${i + 1} (${getOrdinalMonth(currMonth)}) cannot be less than or equal to Row ${i} (${getOrdinalMonth(prevMonth)}).`,
          type: 'error'
        });
        return;
      }
    }

    // Validate total percentage
    const totalPercentage = formData.installments.reduce((sum, inst) => sum + parseFloat(inst.paymentPercentage || 0), 0);
    if (totalPercentage !== 100) {
      setMessage({ text: `Total payment percentage must be 100%. Current: ${totalPercentage}%`, type: 'error' });
      return;
    }

 // 🔹 Normalize installments safely
const normalizeInstallments = (installments = []) => {
  return installments
    .map(inst => ({
      installmentMonth: Number(inst.installmentMonth),
      paymentPercentage: Number(inst.paymentPercentage)
    }))
    .sort((a, b) => a.installmentMonth - b.installmentMonth);
};

const currentInstallments = normalizeInstallments(formData.installments);

// ✅ Duplicate Title Check
const isDuplicateTitle = terms.some(term =>
  term.title.trim().toLowerCase() === formData.title.trim().toLowerCase() &&
  term.id !== editId
);

if (isDuplicateTitle) {
  setMessage({
    text: 'Payment term with this title already exists',
    type: 'error'
  });
  return;
}

// 🚨 Duplicate Installment Structure Check
const isDuplicateInstallment = terms.some(term => {
  if (term.id === editId) return false;

  let existingInstallments = [];

  if (Array.isArray(term.installments)) {
    existingInstallments = term.installments;
  } else if (typeof term.installments === "string") {
    try {
      existingInstallments = JSON.parse(term.installments);
    } catch {
      existingInstallments = [];
    }
  }

  const normalizedExisting = normalizeInstallments(existingInstallments);

  if (normalizedExisting.length !== currentInstallments.length) {
    return false;
  }

  for (let i = 0; i < normalizedExisting.length; i++) {
    if (
      normalizedExisting[i].installmentMonth !== currentInstallments[i].installmentMonth ||
      normalizedExisting[i].paymentPercentage !== currentInstallments[i].paymentPercentage
    ) {
      return false;
    }
  }

  return true;
});

if (isDuplicateInstallment) {
  setMessage({
    text: 'Payment term with same installment structure already exists!',
    type: 'error'
  });
  return;
}

    try {
      setIsSubmitting(true);
      if (editId) {
        await api.paymentTerms.update(editId, formData);
        setMessage({ text: 'Payment term updated successfully!', type: 'success' });
      } else {
        await api.paymentTerms.create(formData);
        setMessage({ text: 'Payment term created successfully!', type: 'success' });
      }
      await fetchTerms();
      setTableRefreshKey(k => k + 1);
      resetForm();
    } catch (error) {
      console.error('Error saving payment term:', error);
      setMessage({ text: error?.message || 'Error saving payment term!', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    if (isSubmitting) return;
    setFormData({ title: '', installments: [{ installmentMonth: '', paymentPercentage: '' }] });
    setEditId(null);
    setShowModal(false);
  };

  const handleEdit = async (term) => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setActionLoadingText("Loading payment term details...");
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      let installments = [{ installmentMonth: '', paymentPercentage: '' }];
      
      if (term.installments) {
        if (Array.isArray(term.installments)) {
          installments = term.installments;
        } else if (typeof term.installments === 'string') {
          try {
            installments = JSON.parse(term.installments);
          } catch (e) {
            console.error('Error parsing installments:', e);
          }
        }
      }
      
      setFormData({ 
        title: term.title, 
        installments: installments
      });
      setEditId(term.id);
      setShowModal(true);
    } catch (error) {
      console.error("Error opening edit payment term:", error);
    } finally {
      setIsActionLoading(false);
    }
  };

const filteredTerms = (terms || []).filter((term) => {

  const matchesTitle =
    !paymentFilters.title ||
    term.title
      .toLowerCase()
      .includes(paymentFilters.title.toLowerCase());

  const matchesInstallment =
    !paymentFilters.installment ||
    (Array.isArray(term.installments) &&
      term.installments.some(
        (inst) =>
          String(inst.installmentMonth) ===
          String(paymentFilters.installment)
      ));

  return matchesTitle && matchesInstallment;
});


    const handleInfo = (term) => {
    setSelectedTerm(term);
    setShowInfoModal(true);
  };

  const handleToggleStatus = async (term) => {
    if (isActionLoading) return;
    const currentStatus = term.status ?? 1;
    const newStatus = currentStatus === 1 ? 2 : 1;
    const isActivating = newStatus === 1;
    const actionText = isActivating ? "activate" : "inactivate";
    const termTitle = term.title || "this payment term";

    if (window.confirm(`Are you sure you want to ${actionText} ${termTitle}?`)) {
      setIsActionLoading(true);
      setActionLoadingText(`${isActivating ? "Activating" : "Inactivating"} payment term...`);
      try {
        await api.paymentTerms.toggleStatus(term.id, newStatus);
        await fetchTerms();
        setTableRefreshKey((k) => k + 1);
        setMessage({
          text: `Payment term ${isActivating ? "activated" : "inactivated"} successfully!`,
          type: "success",
        });
      } catch (error) {
        console.error("Error toggling payment term status:", error);
        setMessage({
          text: error.message || `Failed to ${actionText} payment term`,
          type: "error",
        });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (isActionLoading) return;
    if (window.confirm('Are you sure you want to delete this payment term?')) {
      setIsActionLoading(true);
      setActionLoadingText("Deleting payment term...");
      try {
        await api.paymentTerms.delete(id);
        setMessage({ text: 'Payment term deleted successfully!', type: 'success' });
        await fetchTerms();
      } catch (error) {
        console.error('Error deleting payment term:', error);
        setMessage({ text: 'Error deleting payment term!', type: 'error' });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const addInstallment = () => {
    setFormData({
      ...formData,
      installments: [...formData.installments, { installmentMonth: '', paymentPercentage: '' }]
    });
  };

  const removeInstallment = (index) => {
    if (formData.installments.length > 1) {
      const newInstallments = formData.installments.filter((_, i) => i !== index);
      setFormData({ ...formData, installments: newInstallments });
    }
  };

  const updateInstallment = (index, field, value) => {
    const newInstallments = [...formData.installments];
    newInstallments[index][field] = value;
    setFormData({ ...formData, installments: newInstallments });
  };

  const getTotalPercentage = () => {
    return formData.installments.reduce((sum, inst) => sum + parseFloat(inst.paymentPercentage || 0), 0);
  };

  return (
    <div className="payment-terms">
      {/* Full-screen blocking loader when action is triggered */}
      {isActionLoading && (
        <div
          className="full-screen-action-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            inset: 0,
            width: "100%",
            height: "100%",
            minWidth: "100%",
            minHeight: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            zIndex: 999999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "all",
            cursor: "wait",
          }}
        >
          <div
            className="full-screen-action-card"
            style={{
              background: "#ffffff",
              padding: "18px 28px",
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
              display: "flex",
              alignItems: "center",
              gap: "14px",
              pointerEvents: "auto",
            }}
          >
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "2rem", height: "2rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span
              className="full-screen-action-text"
              style={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#212529",
                letterSpacing: "0.2px",
              }}
            >
              {actionLoadingText || "Processing..."}
            </span>
          </div>
        </div>
      )}

      {message.text && (
        <Message message={message.text} type={message.type} onClose={() => setMessage({ text: '', type: '' })} />
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">

  <h2 className='hd_a'>Payment Terms</h2>

  <div className="d-flex align-items-center gap-2">

    <button
      type="button"
      className={`btn ${
        Object.values(paymentFilters).some(v => v !== "")
          ? "btn-primary"
          : "btn-outline-primary"
      }`}
      onClick={() => setShowFilters(!showFilters)}
    >
      <i className="bi bi-funnel"></i>
    </button>

    <button
      className="btn btn-primary"
      onClick={() => setShowModal(true)}
    >
      <i className="bi bi-plus-lg me-1"></i> Add Payment Term
    </button>

  </div>
</div>
{/* div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className='hd_a'>Payment Terms</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <i className="bi bi-plus-lg me-1"></i> Add Payment Term
        </button>
      </div> */}
      {showFilters && (
  <div className="card shadow-sm mb-3">
    <div className="card-body py-2">

      <div className="row g-3">

        <div className="col-md-4">
          <label className="form-label">Title</label>

          <select
  className="form-control"
  value={paymentFilters.title}
  onChange={(e) =>
    setPaymentFilters({
      ...paymentFilters,
      title: e.target.value,
    })
  }
>
  <option value="">All Titles</option>

  {(terms || []).map((term) => (
    <option key={term.id} value={term.title}>
      {term.title}
    </option>
  ))}
</select>
        </div>

        <div className="col-md-4">
          <label className="form-label">Installment</label>

          <select
            className="form-control"
            value={paymentFilters.installment}
            onChange={(e) =>
              setPaymentFilters({
                ...paymentFilters,
                installment: e.target.value,
              })
            }
          >
            <option value="">All</option>

            {[1,2,3,4,5,6,7,8,9,10,11,12].map((month) => (
              <option key={month} value={month}>
                {getOrdinalMonth(month)}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button
            type="button"
            className="btn btn-outline-primary w-100"
            onClick={() =>
              setPaymentFilters({
                title: "",
                installment: "",
              })
            }
          >
            Clear
          </button>
        </div>

      </div>

    </div>
  </div>
)}

      {showModal && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', pointerEvents: isSubmitting ? 'none' : 'auto' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white py-2">
                <h6 className="modal-title mb-0">{editId ? 'Edit' : 'Add'} Payment Term</h6>
                <button type="button" className="btn-close btn-close-white" disabled={isSubmitting} onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-3">
                  <div className="mb-3">
                    <label className="form-label small mb-1">Title *</label>
                    <input type="text" className="form-control form-control-sm" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                  </div>
                  <div>
                    <label className="form-label small mb-2">Installments</label>
                    <div className="border rounded p-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {formData.installments.map((inst, index) => (
                        <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                          <div style={{ flex: '1' }}>
                            <select
                              className="form-control form-control-sm" 
                              value={inst.installmentMonth} 
                              onChange={(e) => updateInstallment(index, 'installmentMonth', e.target.value)} 
                              required
                            >
                              <option value="">Select Month</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                                <option key={m} value={m}>
                                  {getOrdinalMonth(m)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: '1' }}>
                            <input 
                              type="number" 
                              step="0.01" 
                              min="0" 
                              max="100" 
                              className="form-control form-control-sm" 
                              placeholder="%"
                              value={inst.paymentPercentage} 
                              onChange={(e) => updateInstallment(index, 'paymentPercentage', e.target.value)} 
                              required 
                            />
                          </div>
                          <div className="d-flex gap-1">
                            <button type="button" className="btn btn-sm btn-success px-2 py-1" onClick={addInstallment} title="Add">+</button>
                            {formData.installments.length > 1 && (
                              <button type="button" className="btn btn-sm btn-danger px-2 py-1" onClick={() => removeInstallment(index)} title="Remove">−</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-end mt-2">
                      <small className={getTotalPercentage() === 100 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                        Total: {getTotalPercentage().toFixed(2)}% {getTotalPercentage() === 100 ? '✓' : ''}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer py-2">
                  <button type="button" className="btn btn-sm btn-secondary" disabled={isSubmitting} onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting}>
                    {isSubmitting && (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    )}
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment Term Info Modal */}
      {showInfoModal && selectedTerm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow">
              <div
                className="modal-header py-2"
                style={{
                  backgroundColor: "var(--primary-color, #0d6efd)",
                  color: "#fff",
                }}
              >
                <h6 className="modal-title mb-0">Payment Term Details</h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowInfoModal(false);
                    setSelectedTerm(null);
                  }}
                ></button>
              </div>
              <div className="modal-body p-3">
                <table className="table table-bordered table-sm mb-0">
                  <tbody>
                    <tr>
                      <th style={{ width: "35%", background: "#f8f9fa" }}>Title</th>
                      <td>{selectedTerm.title}</td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>Status</th>
                      <td>
                        {(selectedTerm.status ?? 1) === 1 ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-danger">Inactive</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>Installments</th>
                      <td>
                        {Array.isArray(selectedTerm.installments) && selectedTerm.installments.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {selectedTerm.installments.map((inst, idx) => (
                              <span key={idx} className="badge bg-light text-dark border">
                                {getOrdinalMonth(inst.installmentMonth || inst.month || inst.days)}: {inst.paymentPercentage || inst.percentage}%
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted small">No installments</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th style={{ background: "#f8f9fa" }}>Total Installments</th>
                      <td>
                        {Array.isArray(selectedTerm.installments)
                          ? `${selectedTerm.installments.length} installment(s)`
                          : "0"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer p-2">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setShowInfoModal(false);
                    setSelectedTerm(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body px-0 pt-0 p-6">
          <CommonTable
            key={tableRefreshKey}
            dataSource={api.paymentTerms.table}
            queryParams={{ title: paymentFilters.title, installment: paymentFilters.installment }}
            tableClassName="table table-sm table-hover mb-0"
            columns={[
              { key: 'title', label: 'Title', sortable: true, className: 'align-middle' },
              { key: 'installments', label: 'Installments', sortable: false, className: 'align-middle', render: (value, term) => Array.isArray(term.installments) && term.installments.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {term.installments.map((inst, idx) => (
                    <span key={idx} className="badge bg-light text-dark border">
                      {getOrdinalMonth(inst.installmentMonth)}: {inst.paymentPercentage}%
                    </span>
                  ))}
                </div>
              ) : <span className="text-muted small">No installments</span> },
              {
                key: 'status',
                label: 'Status',
                sortable: true,
                className: 'align-middle',
                render: (value, term) => (
                  (term.status ?? 1) === 1 ? (
                    <span className="badge bg-success" style={{ fontSize: '11px', padding: '4px 8px' }}>Active</span>
                  ) : (
                    <span className="badge bg-danger" style={{ fontSize: '11px', padding: '4px 8px' }}>Inactive</span>
                  )
                )
              }
            ]}
            renderActions={(term) => {
              const isActive = (term.status ?? 1) === 1;
              return (
                <div className="d-flex align-items-center gap-1">
                  <button
                    title="View Details"
                    className="btn btn-sm btn-primary"
                    disabled={isActionLoading}
                    onClick={() => handleInfo(term)}
                    style={{ fontSize: "13px", padding: "4px 10px" }}
                  >
                    <i className="bi bi-info-circle-fill"></i>
                  </button>
                  {isActive && (
                    <button
                      title="Edit"
                      className="btn btn-sm btn-primary"
                      disabled={isActionLoading}
                      onClick={() => handleEdit(term)}
                      style={{ fontSize: "13px", padding: "4px 10px" }}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </button>
                  )}
                  {/* Hard delete commented out as per soft delete requirement
                  <button
                    title="Delete"
                    className="btn btn-sm btn-danger"
                    disabled={isActionLoading}
                    onClick={() => handleDelete(term.id)}
                    style={{ fontSize: "13px", padding: "4px 10px" }}
                  >
                    <i className="bi bi-trash-fill"></i>
                  </button>
                  */}
                  <button
                    title={isActive ? "Inactivate Payment Term" : "Activate Payment Term"}
                    className={`btn btn-sm ${
                      isActive ? "btn-outline-danger" : "btn-outline-success"
                    }`}
                    onClick={() => handleToggleStatus(term)}
                    disabled={isActionLoading}
                    style={{ fontSize: "13px", padding: "4px 10px" }}
                  >
                    {isActive ? (
                      <i className="bi bi-slash-circle" title="Inactivate"></i>
                    ) : (
                      <i className="bi bi-check-circle" title="Activate"></i>
                    )}
                  </button>
                </div>
              );
            }}
            emptyMessage="No payment terms found"
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentTerms;
